import React from "react";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    icon?: React.ReactNode;
    hint?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ className = "", label, error, icon, hint, ...props }, ref) => {
        return (
            <div className="w-full space-y-1.5">
                {label && (
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {label}
                        {props.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                )}
                <div className="relative">
                    {icon && (
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
                            {icon}
                        </div>
                    )}
                    <input
                        className={`
                            flex h-11 w-full rounded-lg border bg-white dark:bg-slate-900
                            px-3 py-2 text-sm text-slate-800 dark:text-slate-100
                            ring-offset-background transition-all duration-200
                            file:border-0 file:bg-transparent file:text-sm file:font-medium
                            placeholder:text-slate-400 dark:placeholder:text-slate-500
                            focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-900
                            disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800
                            ${icon ? 'pl-10' : ''}
                            ${error
                                ? "border-red-500 focus:ring-red-500/20 focus:border-red-500"
                                : "border-slate-300 dark:border-slate-700"
                            }
                            ${className}
                        `}
                        ref={ref}
                        {...props}
                    />
                </div>
                {hint && !error && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>
                )}
                {error && (
                    <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {error}
                    </p>
                )}
            </div>
        );
    }
);
Input.displayName = "Input";

// Textarea Component
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string;
    error?: string;
    hint?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ className = "", label, error, hint, ...props }, ref) => {
        return (
            <div className="w-full space-y-1.5">
                {label && (
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 leading-none">
                        {label}
                        {props.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                )}
                <textarea
                    className={`
                        flex min-h-[100px] w-full rounded-lg border bg-white dark:bg-slate-900
                        px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100
                        ring-offset-background transition-all duration-200
                        placeholder:text-slate-400 dark:placeholder:text-slate-500
                        focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900
                        disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800
                        resize-none
                        ${error
                            ? "border-red-500 focus:ring-red-500/20 focus:border-red-500"
                            : "border-slate-200 dark:border-slate-700"
                        }
                        ${className}
                    `}
                    ref={ref}
                    {...props}
                />
                {hint && !error && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>
                )}
                {error && (
                    <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {error}
                    </p>
                )}
            </div>
        );
    }
);
Textarea.displayName = "Textarea";

// Select Component
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
    error?: string;
    options: { value: string; label: string }[];
    placeholder?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
    ({ className = "", label, error, options, placeholder, ...props }, ref) => {
        return (
            <div className="w-full space-y-1.5">
                {label && (
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 leading-none">
                        {label}
                        {props.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                )}
                <div className="relative">
                    <select
                        className={`
                            flex h-11 w-full rounded-lg border bg-white dark:bg-slate-900
                            px-3 py-2 text-sm text-slate-900 dark:text-slate-100
                            ring-offset-background transition-all duration-200
                            focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900
                            disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800
                            appearance-none cursor-pointer
                            ${error
                                ? "border-red-500 focus:ring-red-500/20 focus:border-red-500"
                                : "border-slate-200 dark:border-slate-700"
                            }
                            ${className}
                        `}
                        ref={ref}
                        {...props}
                    >
                        {placeholder && (
                            <option value="" disabled>
                                {placeholder}
                            </option>
                        )}
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 dark:text-slate-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
                {error && (
                    <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {error}
                    </p>
                )}
            </div>
        );
    }
);
Select.displayName = "Select";
